import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "../config/authConfig";
import { environment } from "../config/environment";
import { msalInstance } from "../config/msalInstance";

/** Config con la marca de reintento que agrega el interceptor de 401 */
interface IRetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const api = axios.create({
  baseURL: environment.API_URL,
});

// REQUEST INTERCEPTOR: obtiene un token fresco de MSAL antes de cada request.
// MSAL devuelve el token de su cache si sigue vigente, o lo renueva de forma
// transparente. Asi el token nunca se lee vencido desde sessionStorage.
api.interceptors.request.use(async (config) => {
  const account = msalInstance.getActiveAccount();
  if (!account) return config;

  try {
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
    });

    sessionStorage.setItem("token", response.idToken);
    config.headers.Authorization = `Bearer ${response.idToken}`;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
    }
  }

  return config;
});

// RESPONSE INTERCEPTOR: maneja 401 con un reintento de refresco forzado
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as IRetriableConfig | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;
      const account = msalInstance.getActiveAccount();

      if (account) {
        try {
          const response = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account,
            forceRefresh: true,
          });

          sessionStorage.setItem("token", response.idToken);
          originalRequest.headers.Authorization = `Bearer ${response.idToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          if (refreshError instanceof InteractionRequiredAuthError) {
            sessionStorage.clear();
            localStorage.clear();
            await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
          }
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
